import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  BroadcastService,
  MsalAngularConfiguration,
  MsalInterceptor,
  MsalModule,
  MsalService,
  MSAL_CONFIG,
  MSAL_CONFIG_ANGULAR
} from '@azure/msal-angular';
import { RegistrationModule } from '@features/registration/registration.module';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthModule } from '@services/auth/auth.module';
import { CoreModule } from '@services/core/core.module';
import { AppSettings } from '@view-models/config/app-settings';
import { NavbarModule } from '@widgets/site-common/navbar/navbar.module';
import { UiCommonModule } from '@widgets/ui-common/ui.common.module';
import { BreadcrumbModule } from 'angular-crumbs';
import { Configuration } from 'msal';
import { NgHttpLoaderModule } from 'ng-http-loader';
import { NgxMaskModule } from 'ngx-mask';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorPagesModule } from './shared/error-pages/error-pages.module';

export function MSALConfigFactory(locale: string, settings: AppSettings): Configuration {
  const redirectUri = settings.auth.redirectUri;
  const postLogoutRedirectUri = settings.auth.postLogoutRedirectUri;

  return {
    auth: {
      clientId: settings.auth.clientId,
      authority: settings.auth.loginAuthority,
      validateAuthority: false,
      redirectUri: `${redirectUri || window.location.origin}`,
      postLogoutRedirectUri: `${postLogoutRedirectUri || window.location.origin}`,
      navigateToLoginRequestUrl: true
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false // set to true for IE 11
    }
  };
}

export function MSALAngularConfigFactory(settings: AppSettings): MsalAngularConfiguration {
  const protectedResourceMap: [string, string[]][] = [
    ['https://graph.microsoft.com/v1.0/me', ['user.read']],
    [settings.api.baseUri, [settings.auth.clientId]]
  ];

  return {
    popUp: false,
    consentScopes: settings.auth.consentScopes,
    unprotectedResources: ['appsettings.json'],
    protectedResourceMap,
    extraQueryParameters: {}
  };
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    AuthModule,
    AppRoutingModule,
    BreadcrumbModule,
    BrowserAnimationsModule,
    BrowserModule,
    CoreModule,
    ErrorPagesModule,
    NavbarModule,
    NgxMaskModule.forRoot(),
    UiCommonModule,
    NgHttpLoaderModule.forRoot(),
    RegistrationModule,
    MsalModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_CONFIG,
      useFactory: MSALConfigFactory,
      deps: [LOCALE_ID, AppSettings]
    },
    {
      provide: MSAL_CONFIG_ANGULAR,
      useFactory: MSALAngularConfigFactory,
      deps: [AppSettings]
    },
    MsalService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private msalService: MsalService, private broadcastService: BroadcastService) {
    this.msalService.handleRedirectCallback((authError, response) => {
      if (authError) {
        console.error(authError);
      }
    });
    this.broadcastService.subscribe('msal:loginFailure', payload => {
      console.error('msal:loginFailure');
      console.error(payload);
    });
    this.broadcastService.subscribe('msal:acquireTokenFailure', payload => {
      console.error('msal:acquireTokenFailure');
      console.error(payload);
    });
    this.broadcastService.subscribe('msal:ssoFailure', payload => {
      console.error('msal:ssoFailure');
      console.error(payload);
    });
    this.broadcastService.subscribe('msal:loginSuccess', payload => {
    });
    this.broadcastService.subscribe('msal:acquireTokenSuccess', payload => {
    });
    this.broadcastService.subscribe('msal:ssoSuccess', payload => {
    });
  }
}
